import React, {
  useState, useMemo, useLayoutEffect, useRef, useEffect,
  useImperativeHandle, forwardRef
} from 'react';
import './Summary.css';
import { summaryAPI } from '../../services/api';
import { transformSummaryDevices } from '../../utils/dataTransformers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle, faSort, faSortUp, faSortDown, faTimes
} from '@fortawesome/free-solid-svg-icons';
import DeviceFilterPopup from './DeviceFilterPopup';

const TABLE_VIEWPORT_HEIGHT = 700;

const parseTop3 = (arr = []) =>
  arr.map((line) => {
    const parts = String(line).trim().split(/:\s*/);
    if (parts.length === 2) {
      return { type: parts[0], count: parseInt(parts[1]) || 0 };
    }
    return { type: String(line), count: '' };
  });

const DeviceTable = forwardRef(function DeviceTable({ query = '', dateRange = null }, ref) {
  const rowsPerPage = 10;
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'desc' });
  const [selectedRow, setSelectedRow] = useState(null);
  const [deviceRows, setDeviceRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState([]);
  const [globalFilter, setGlobalFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    globalOptions: [],
    clientOptions: []
  });

  const [showFilter, setShowFilter] = useState(false);
  const tableRef = useRef(null);
  const [colWidths, setColWidths] = useState(null);

  useImperativeHandle(ref, () => ({ openFilterPopup: () => setShowFilter(true) }));

  // Fetch data when dateRange changes
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [devicesRes, filterRes] = await Promise.all([
          summaryAPI.getAllDevices(dateRange),
          summaryAPI.getFilterOptions()
        ]);
        
        setDeviceRows(transformSummaryDevices(devicesRes.data));
        setFilterOptions(filterRes.data);
      } catch (error) {
        console.error('Failed to fetch devices:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [dateRange]);

  const columns = [
    { key: 'startDate', label: 'Start Date' },
    { key: 'global', label: 'Global' },
    { key: 'region', label: 'Region' },
    { key: 'client', label: 'Client' },
    { key: 'address', label: 'Address' },
    { key: 'location', label: 'Location' },
    { key: 'deviceName', label: 'Device Name' },
    { key: 'deviceId', label: 'Device ID' },
    { key: 'activity', label: 'Activity' },
    { key: 'status', label: 'Status' },
  ];

  // Search + Filters
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return deviceRows.filter((d) => {
      const matchesSearch = !q || [
        d.global, d.region, d.client, d.address, d.location,
        d.deviceName, d.deviceId, d.status,
        ...(d.top3 || [])
      ].some((v) => v?.toString().toLowerCase().includes(q));

      if (!matchesSearch) return false;
      if (statusFilter.length > 0 && !statusFilter.includes(d.status)) return false;
      if (globalFilter.length > 0 && !globalFilter.includes(d.global)) return false;
      if (clientFilter.length > 0 && !clientFilter.includes(d.client)) return false;

      return true;
    });
  }, [query, statusFilter, globalFilter, clientFilter, deviceRows]);

  // Sorting
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      const key = sortConfig.key;

      if (key === 'total') return dir * ((a.total || 0) - (b.total || 0));
      if (key === 'startDate') {
        const dateA = new Date(a.startDate.split('-').reverse().join('-'));
        const dateB = new Date(b.startDate.split('-').reverse().join('-'));
        return dir * (dateA - dateB);
      }

      const aVal = (a[key] || '').toString().toLowerCase();
      const bVal = (b[key] || '').toString().toLowerCase();
      return aVal < bVal ? -dir : aVal > bVal ? dir : 0;
    });
    return arr;
  }, [filtered, sortConfig]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const start = (page - 1) * rowsPerPage;
  const currentPageRows = sorted.slice(start, start + rowsPerPage);

  const goToPage = (p) => p >= 1 && p <= totalPages && setPage(p);
  const requestSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
    setPage(1);
  };
  const iconFor = (key) =>
    sortConfig.key !== key ? faSort : (sortConfig.direction === 'asc' ? faSortUp : faSortDown);

  useLayoutEffect(() => {
    if (colWidths || loading) return;
    const table = tableRef.current;
    if (!table) return;
    const firstRow = table.querySelector('tbody tr');
    if (!firstRow) return;
    const widths = Array.from(firstRow.querySelectorAll('td'))
      .map((cell) => `${cell.getBoundingClientRect().width}px`);
    setColWidths(widths);
  }, [colWidths, loading]);

  const totalColumns = columns.length + 4;

  const effectiveColWidths = useMemo(() => {
    if (!colWidths || colWidths.length < totalColumns) return null;
    const w = [...colWidths];
    w[totalColumns - 1] = '32px';
    return w;
  }, [colWidths, totalColumns]);

  const openDetails = (row) => setSelectedRow(row);
  const closeDetails = () => setSelectedRow(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { closeDetails(); setShowFilter(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleStatus = (status) => {
    setStatusFilter(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };
  const toggleGlobal = (global) => {
    setGlobalFilter(prev =>
      prev.includes(global) ? prev.filter(c => c !== global) : [...prev, global]);
  };
  const toggleClient = (client) => {
    setClientFilter(prev =>
      prev.includes(client) ? prev.filter(c => c !== client) : [...prev, client]);
  };

  const resetAllFilters = () => {
    setStatusFilter([]);
    setGlobalFilter([]);
    setClientFilter([]);
  };

  if (loading) {
    return <div className="sum-table-wrap" style={{ padding: '40px', textAlign: 'center' }}>Loading devices...</div>;
  }

  return (
    <div className="sum-table-wrap">
      <div
        className="sum-table-viewport"
        style={{
          height: TABLE_VIEWPORT_HEIGHT,
          overflow: 'auto',
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10
        }}
      >
        <table className="sum-table colored-columns" ref={tableRef}>
          {effectiveColWidths && (
            <colgroup>
              {effectiveColWidths.slice(0, totalColumns).map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
          )}

          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} className={col.key === 'status' ? 'status-cell' : ''}>
                  <button type="button" className="sort-btn" onClick={() => requestSort(col.key)}>
                    {col.label}
                    <FontAwesomeIcon icon={iconFor(col.key)} className="sort-ic" />
                  </button>
                </th>
              ))}
              <th colSpan="3" className="merged-header">
                <button type="button" className="sort-btn" onClick={() => requestSort('total')}>
                  Insect Trapped (Total | Top 3)
                  <FontAwesomeIcon icon={iconFor('total')} className="sort-ic" />
                </button>
              </th>
              <th className="info-header"></th>
            </tr>
          </thead>

          <tbody>
            {currentPageRows.map((row) => {
              const top3 = parseTop3(row.top3 || []);
              return (
                <tr key={row.id}>
                  <td>{row.startDate}</td>
                  <td>{row.global}</td>
                  <td>{row.region}</td>
                  <td>{row.client}</td>
                  <td className="wrap">{row.address}</td>
                  <td>{row.location}</td>
                  <td>{row.deviceName}</td>
                  <td>{row.deviceId}</td>
                  <td>{row.activity}</td>
                  <td className="status-cell">
                    <span
                      className={`status-badge ${
                        row.status === 'ONLINE' ? 'st-online'
                          : row.status === 'OFFLINE' ? 'st-offline'
                          : 'st-maint'
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>

                  <td className="it3-total">
                    <strong>{row.total?.toLocaleString('en-US')}</strong>
                  </td>
                  <td className="it3-type-col">
                    {top3.map((s, i) => <div key={i}>{s.type}</div>)}
                  </td>
                  <td className="it3-count-col">
                    {top3.map((s, i) => (
                      <div key={i}>
                        {s.count !== '' ? s.count.toLocaleString('en-US') : ''}
                      </div>
                    ))}
                  </td>

                  <td className="info-cell">
                    <button
                      type="button"
                      className="info-icon-btn"
                      aria-label="View details"
                      onClick={() => openDetails(row)}
                    >
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {currentPageRows.length === 0 && (
              <tr>
                <td colSpan={totalColumns} style={{ textAlign: 'center', padding: 20 }}>
                  No results
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="sum-pagination">
        <button onClick={() => goToPage(page - 1)} disabled={page === 1}>Prev</button>
        <span>Page {page} of {totalPages}</span>
        <button onClick={() => goToPage(page + 1)} disabled={page === totalPages}>Next</button>
      </div>

      {selectedRow && (
  <div className="sum-modal-overlay" onClick={closeDetails} role="presentation">
    <div className="sum-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
      <div className="sum-modal-header">
        <div className="sum-modal-title">{selectedRow.deviceName} • {selectedRow.deviceId}</div>
        <button className="sum-modal-close" onClick={closeDetails} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      <div className="sum-modal-body">
        {/* Left Column - Device Info */}
        <div className="sum-modal-col">
          <div><strong>Start Date:</strong> {selectedRow.startDate}</div>
          <div><strong>Global:</strong> {selectedRow.global}</div>
          <div><strong>Region:</strong> {selectedRow.region}</div>
          <div><strong>Client:</strong> {selectedRow.client}</div>
          <div className="wrap"><strong>Address:</strong> {selectedRow.address}</div>
          <div><strong>Location:</strong> {selectedRow.location}</div>
          <div><strong>Device Name:</strong> {selectedRow.deviceName}</div>
          <div><strong>Device ID:</strong> {selectedRow.deviceId}</div>
          <div><strong>Activity:</strong> {selectedRow.activity}</div>
          <div><strong>Status:</strong> {selectedRow.status}</div>
        </div>

        {/* Right Column - All Species */}
        <div className="sum-modal-col">
          <div style={{ marginBottom: 12 }}>
            <strong>Insect Species Capture:</strong>
          </div>
          {(selectedRow.allSpecies || []).map((species, i) => (
            <div key={i} style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
            }}>
              <span>{species.species_name}</span>
              <strong>{species.total_count.toLocaleString('en-US')}</strong>
            </div>
          ))}
          <div style={{ 
            marginTop: 12, 
            paddingTop: 12, 
            borderTop: '2px solid #0A3760',
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '16px'
          }}>
            <strong>Total Insects:</strong> 
            <strong>{selectedRow.total?.toLocaleString('en-US')}</strong>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

      {showFilter && (
        <DeviceFilterPopup
          statusOptions={['ONLINE', 'OFFLINE', 'MAINT REQD']}
          globalOptions={filterOptions.globalOptions}
          clientOptions={filterOptions.clientOptions}
          statusFilter={statusFilter}
          globalFilter={globalFilter}
          clientFilter={clientFilter}
          onToggleStatus={toggleStatus}
          onToggleGlobal={toggleGlobal}
          onToggleClient={toggleClient}
          onReset={resetAllFilters}
          onClose={() => setShowFilter(false)}
        />
      )}
    </div>
  );
});

export default DeviceTable;